import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { Box, Typography, IconButton, Select, MenuItem } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from '@mui/styles';

// Utils
import { makeReaderListColumn } from '../../../../utils/columns/makeReaderListColumn';
import { FormatForApi, shortDateFormat } from '../../../../utils/Format';
import { Storage } from '../../../../utils/Storage';

// Hooks & Services
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';
import { fetchAllSettings } from '../../../../redux/slices/settingsSlice';
import { getReaders } from '../../../../services/reader.service';

// Types
import { ReadersStationMapView, SearchBarFilterTypes } from '../../../../constants/Constants';
import { FilterableTypes } from '../../../../types/FilterableTypes';
import { Reader } from '../../../../types/Reader';
import { PaginatedResult } from '../../../../types/PaginatedResult';
import { SelectedStation } from './ReadersMapView';

// Common Components
import SearchBar from '../../../common/SearchBar/SearchBar';
import FormInputCollection from '../../../common/FormInputCollection/FormInputCollection';
import { Table, useNewTable } from '../../../common/table/index';

const useStyles = makeStyles(() => ({
  modalContent: {
    position: 'relative',
    backgroundColor: '#fff',
    boxShadow: '-3px 0 5px rgba(0, 0, 0, 0.2)',
    display: 'flex',
    flexDirection: 'column',
    padding: 16,
    overflowY: 'auto',
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
  },
}));

const ReadersByStationIdModal = ({
  isOpen,
  onClose,
  station,
  onFetchReaders,
}: {
  isOpen: boolean;
  onClose: () => void;
  station: SelectedStation | null;
  onFetchReaders: (readers: Reader[]) => void;
}) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const settingStates = useAppSelector((state) => state.settings);

  const [supplyChainStatus, setSupplyChainStatus] = useState<number | undefined>();
  const [opsStatus, setOpsStatus] = useState<number | undefined>();
  const [isMobile, setIsMobile] = useState<number | undefined>();
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [paramFilter, setParamFilter] = useState<any>({
    filters: [{ stationId: station?.id }],
    sortBy: [{ lastSeenDateTime: '-1' }],
  });

  useEffect(() => {
    if (station?.id) {
      setParamFilter((prevFilter) => ({
        ...prevFilter,
        filters: [
          {
            ...prevFilter.filters[0],
            stationId: station.id,
          },
        ],
      }));
    }
  }, [station?.id]);

  const intialDefaultFilters = () => {
    setParamFilter({
      filters: [
        {
          stationId: station?.id,
        },
      ],
      sortBy: paramFilter.sortBy,
    });
  };

  useEffect(() => {
    dispatch(fetchAllSettings());
    intialDefaultFilters();
  }, [station?.id]);

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({
        filters: [{ stationId: station?.id }],
        sortBy: [{ lastSeenDateTime: '-1' }],
      });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const columns = useMemo(() => makeReaderListColumn(settingStates), [settingStates]);

  const table = useNewTable<Reader>({
    columns,
    fetchAsync: getReaders,
    params: paramFilter,
    serverSide: true,
    onFetchComplete: (res: PaginatedResult<Reader>) => onFetchReaders(res.results),
  });

  const onSearchChange = (filters: any[]) => {
    const finalFilters = [...filters, { stationId: station?.id }];

    if (supplyChainStatus !== undefined) {
      finalFilters.push({
        supplyChainStatus: supplyChainStatus,
        filterType: FilterableTypes.Equals,
      });
    }

    if (opsStatus !== undefined) {
      finalFilters.push({
        opsStatus: opsStatus,
        filterType: FilterableTypes.Equals,
      });
    }

    if (isMobile !== undefined) {
      finalFilters.push({
        isMobile: isMobile,
        filterType: FilterableTypes.Equals,
      });
    }

    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      finalFilters.push({
        lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    setParamFilter({ filters: finalFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.READERS, { isMoreFilter: true, filters: finalFilters });
  };

  const handleReset = () => {
    setIsResetting(true);
    setSupplyChainStatus(undefined);
    setOpsStatus(undefined);
    setIsMobile(undefined);
    setLastSeenDateTimeFrom(null);
    setLastSeenDateTimeTo(null);

    setParamFilter({
      filters: [{ stationId: station?.id }],
      sortBy: paramFilter.sortBy,
    });

    Storage.setSearchBarFiltering(SearchBarFilterTypes.READERS, {
      isMoreFilter: false,
      filters: [{ stationId: station?.id }],
    });
  };

  return (
    <Box className={`${classes.modalContent}`} role='dialog' aria-labelledby='modal-title'>
      <IconButton aria-label='close' className={classes.closeButton} onClick={onClose}>
        <CloseIcon />
      </IconButton>
      <Typography id='modal-title' variant='h6' component='h2'>
        {station?.name}
      </Typography>
      <Typography variant='body1'>
        {ReadersStationMapView.readerCount} {station?.readerCount}
      </Typography>

      <SearchBar
        pageKey={SearchBarFilterTypes.READERS}
        onSearch={onSearchChange}
        onReset={handleReset}
        columns={columns}
        allowSearchAll={true}
        components={
          <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 5 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <FormInputCollection label='Supply Chain' style={{ marginBottom: '16px' }}>
                <Select
                  value={supplyChainStatus ?? 'all'}
                  variant='outlined'
                  id='supplyChainStatus'
                  name='supplyChainStatus'
                  size='small'
                  style={{ width: '150px' }}
                  onChange={(e: any) => {
                    setSupplyChainStatus(e.target.value === 'all' ? undefined : e.target.value);
                  }}
                >
                  <MenuItem value={'all'}>All</MenuItem>
                  {settingStates.readerSupplyChainStatus?.value.map((val, index: number) => {
                    return (
                      <MenuItem key={index} value={val.text}>
                        {val.value}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormInputCollection>
              <FormInputCollection label='Ops Status' style={{ marginLeft: '16px', marginBottom: '16px' }}>
                <Select
                  value={opsStatus ?? 'all'}
                  variant='outlined'
                  id='opsStatus'
                  name='opsStatus'
                  size='small'
                  style={{ width: '150px' }}
                  onChange={(e: any) => {
                    setOpsStatus(e.target.value === 'all' ? undefined : e.target.value);
                  }}
                >
                  <MenuItem value={'all'}>All</MenuItem>
                  {settingStates.readerOpsStatus?.value.map((val, index: number) => {
                    return (
                      <MenuItem key={index} value={val.text}>
                        {val.value}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormInputCollection>
              <FormInputCollection label='Mobile' style={{ marginLeft: '16px', marginBottom: '16px' }}>
                <Select
                  value={isMobile ?? 'all'}
                  variant='outlined'
                  id='isMobile'
                  name='isMobile'
                  size='small'
                  style={{ width: '150px' }}
                  onChange={(e: any) => {
                    setIsMobile(e.target.value === 'all' ? undefined : e.target.value);
                  }}
                >
                  <MenuItem value={'all'}>All</MenuItem>
                  <MenuItem value={1}>Yes</MenuItem>
                  <MenuItem value={0}>No</MenuItem>
                </Select>
              </FormInputCollection>
              <FormInputCollection label='Last Seen date' style={{ marginLeft: '16px', marginBottom: '16px' }}>
                <div style={{ marginRight: '8px' }}>
                  <DatePicker
                    className='searchbar-datepicker'
                    value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                    label='From'
                    format={shortDateFormat}
                    slotProps={{ textField: { size: 'small' } }}
                    onChange={(date) => {
                      setLastSeenDateTimeFrom(date ? date.toDate() : null);
                    }}
                  />
                </div>
                <DatePicker
                  className='searchbar-datepicker'
                  value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                  label='To'
                  format={shortDateFormat}
                  slotProps={{ textField: { size: 'small' } }}
                  onChange={(date) => {
                    setLastSeenDateTimeTo(date ? date.toDate() : null);
                  }}
                />
              </FormInputCollection>
            </LocalizationProvider>
          </div>
        }
      />
      <Table height='300px' {...table} showSearchBar={false} showColumnToggler={false} getCoreRowModel={getCoreRowModel()} />
    </Box>
  );
};

export default ReadersByStationIdModal;
